import { Box, Button } from "@mui/material";
import type { PropsWithChildren } from "react";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  PageLayout,
} from "../../api/types/objectTypes";
import PanelSection from "../common/layout/PanelSection";
import PanelSectionHeader from "../common/layout/PanelSectionHeader";
import FieldValuesContainer from "./FieldValuesContainer";
import type { EmailFieldEditMode } from "./FieldValuesContext";
import type { FieldValuesEditor } from "./useFieldValuesEditor";

interface Props {
  editor: FieldValuesEditor;
  objectType: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  allowEditing: boolean;
  emailFieldEditMode?: EmailFieldEditMode;
  isWaitingForValues?: boolean;
  fieldsAutoFillTooltips?: Record<string, string>;
}

const FieldValuesEditorView = ({
  editor,
  objectType,
  businessCentralType,
  fields,
  cardLayout,
  fieldValues,
  fieldsConfidence,
  fieldsCitations,
  lookupObjects,
  allowEditing,
  emailFieldEditMode,
  isWaitingForValues,
  fieldsAutoFillTooltips,
  children,
}: PropsWithChildren<Props>) => {
  const { fieldValuesState, lookupOptionsByObjectType } = editor;

  return (
    <PanelSection isLoading={editor.isBusy}>
      <PanelSectionHeader title="Details">
        {allowEditing && (
          <>
            <Button
              disabled={editor.areButtonsDisabled}
              onClick={editor.handleCancelClick}
              variant="text"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              loading={isWaitingForValues === true}
              disabled={editor.isSaveDisabled || isWaitingForValues === true}
              onClick={editor.handleSaveClick}
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
          </>
        )}
      </PanelSectionHeader>
      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2 }}>
        <FieldValuesContainer
          objectType={objectType}
          businessCentralType={businessCentralType}
          fields={fields}
          cardLayout={cardLayout}
          initialFieldValues={fieldValues}
          lookupObjects={lookupObjects}
          lookupOptionsByObjectType={lookupOptionsByObjectType}
          onAddLookupOption={editor.handleAddLookupOption}
          fieldsConfidence={fieldsConfidence}
          fieldsCitations={fieldsCitations}
          allowEditing={allowEditing}
          fieldValuesState={fieldValuesState}
          onFieldValuesStateChange={editor.handleFieldValuesStateChange}
          emailFieldEditMode={emailFieldEditMode}
          fieldsAutoFillTooltips={fieldsAutoFillTooltips}
        />

        {children}
      </Box>
    </PanelSection>
  );
};

export default FieldValuesEditorView;
